import { Form, FormRenderProps } from 'react-final-form';
import { Stack, Button, CircularProgress, Card, CardContent, Typography } from '@mui/material';

import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { KioskDefaultScreenFragment } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';

import { DefaultScreenForm } from '../../components/default-screen-form/default-screen-form.component';
import { validate } from '../../utils/default-screen-form.validation';
import { DefaultScreenFormValues } from '../../touchpoint.types';
import {
  SAVE_BUTTON_TEXT,
  DEFAULT_SCREEN_DETAILS_BACK_BUTTON_TEXT,
  DEFAULT_SCREENS,
  DEFAULT_SCREENS_PAGE_SUBTITLE,
} from '../../touchpoint.constants';

type DefaultScreensEditPageProps = {
  loading: boolean;
  error?: Error;
  defaultScreen?: KioskDefaultScreenFragment | null;
  breadcrumbLinks: LinkItem[];
  initialValues: Partial<DefaultScreenFormValues>;
  onSubmit: (values: DefaultScreenFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  id?: string;
};

export const DefaultScreensEditPage = ({
  loading,
  error,
  defaultScreen,
  breadcrumbLinks,
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  id,
}: DefaultScreensEditPageProps) => {
  useAppLayoutContextConsumer(breadcrumbLinks);

  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.TOUCHPOINT_DEFAULT_SCREEN_EDIT);

  const renderForm = ({
    handleSubmit,
    valid,
    submitting,
  }: FormRenderProps<DefaultScreenFormValues>) => {
    const isDisabled = submitting || isSubmitting || !valid;

    const actions = isUserPermittedToEdit ? (
      <Stack direction='row' spacing={1}>
        <Button variant='contained' color='secondary' onClick={handleSubmit} disabled={isDisabled}>
          {SAVE_BUTTON_TEXT}
        </Button>
      </Stack>
    ) : null;

    return (
      <Page
        browserTabTitle={DEFAULT_SCREENS}
        pageHeaderProps={{
          title: DEFAULT_SCREENS,
          subtitle: DEFAULT_SCREENS_PAGE_SUBTITLE,
        }}
        actions={actions}
      >
        <CanEditFormFields isEditable={isUserPermittedToEdit}>
          <DefaultScreenForm isEditMode handleSubmit={handleSubmit} defaultScreenId={id} />
        </CanEditFormFields>
      </Page>
    );
  };

  if (loading) {
    return (
      <Page
        browserTabTitle={DEFAULT_SCREENS}
        pageHeaderProps={{
          title: DEFAULT_SCREENS,
          subtitle: 'Loading...',
        }}
      >
        <Card>
          <CardContent>
            <CircularProgress />
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (error || !defaultScreen) {
    return (
      <Page
        browserTabTitle={DEFAULT_SCREENS}
        pageHeaderProps={{
          title: DEFAULT_SCREENS,
          subtitle: 'Error',
        }}
      >
        <Card>
          <CardContent>
            <Typography color='error'>{error?.message || 'Default screen not found'}</Typography>
            <Button onClick={onCancel} sx={{ mt: 2 }}>
              {DEFAULT_SCREEN_DETAILS_BACK_BUTTON_TEXT}
            </Button>
          </CardContent>
        </Card>
      </Page>
    );
  }

  return (
    <Form<DefaultScreenFormValues>
      onSubmit={onSubmit}
      render={renderForm}
      validate={validate}
      initialValues={initialValues}
      keepDirtyOnReinitialize
    />
  );
};
